import { useState, useEffect, useCallback, useRef } from 'react';
import { UnifiedAuthenticationService } from '../lib/UnifiedAuthenticationService';
import type { AuthConfig, AuthUser } from '../types';

export const useAuthenticationService = ({
  provider = 'firebase',
  firebaseAuth = null,
  msalConfig = null,
  tokenRefreshInterval = 50 * 60 * 1000,
  backendUrl = null as string | null,
  onError = ((_: any) => {}) as (error: any) => void,
  logger = console,
}: AuthConfig & { backendUrl?: string | null; onError?: (error: any) => void }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authServiceRef = useRef<UnifiedAuthenticationService | null>(null);
  const verifyInProgressRef = useRef(false);
  const backendVerifiedRef = useRef(false);

  useEffect(() => {
    // Reset verification state on re-initialization
    backendVerifiedRef.current = false;

    const authService = new UnifiedAuthenticationService({
      provider,
      firebaseAuth,
      msalConfig,
      tokenRefreshInterval,
      onTokenRefresh: (newToken) => {
        if ('debug' in (logger || {}) && typeof (logger as any).debug === 'function') {
          (logger as any).debug('Token refreshed via hook');
        }
        setIdToken(newToken);
      },
      onAuthStateChange: (user, token) => {
        setUser(user);
        setIdToken(token);
        setIsLoading(false);
        // Only verify with backend on initial auth, not on token refreshes triggered
        // by verification itself (which would cause an infinite loop)
        if (user && token && backendUrl && !verifyInProgressRef.current && !backendVerifiedRef.current) {
          verifyTokenWithBackend(token);
        }
      },
      logger,
    });
    authServiceRef.current = authService;
    
    // Failsafe: ensure loading state is cleared after a reasonable timeout
    // This prevents infinite loading if auth state detection fails.
    // Azure initialization can take longer on cold loads; allow more time.
    const timeoutMs = provider === 'azure' ? 15000 : 3000;
    const loadingTimeout = setTimeout(() => {
      logger.warn('Authentication state detection timeout - clearing loading state');
      setIsLoading(false);
    }, timeoutMs);
    
    return () => {
      authServiceRef.current?.destroy();
      clearTimeout(loadingTimeout);
    };
  }, [provider, firebaseAuth, msalConfig, tokenRefreshInterval, backendUrl, logger]);

  const verifyTokenWithBackend = useCallback(async (token: string) => {
    if (!backendUrl) return;
    // Prevent re-entrant calls: getToken(true) inside this function triggers
    // acquireTokenSilent → onAuthStateChange → verifyTokenWithBackend again
    if (verifyInProgressRef.current) {
      logger.info('[AzureAuth] Backend verification already in progress, skipping');
      return;
    }
    verifyInProgressRef.current = true;
    try {
      logger.info('[AzureAuth] Verifying token with backend');
      const response = await fetch(`${backendUrl}/verifyToken/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({}),  // Safari requires a body when Content-Type is application/json
      });
      if (!response.ok) {
        // Check if this is an authentication error vs other errors
        if (response.status === 403) {
          // User is not authorized - don't retry, just logout with specific message
          let errorMessage = 'User not authorized to access this application';
          try {
            const errorData = await response.json();
            if (errorData.error) errorMessage = errorData.error;
          } catch {}
          logger.error('[AzureAuth] User not authorized (403) - forcing logout');
          await authServiceRef.current?.signOut();
          throw new Error(errorMessage);
        }
        if (response.status === 401) {
          // Token is invalid or expired - try to refresh
          logger.warn('[AzureAuth] Backend returned 401, attempting token refresh');
          const newToken = await authServiceRef.current?.getToken(true); // Force refresh
          if (newToken) {
            // Retry verification with new token
            logger.info('[AzureAuth] Got new token, retrying backend verification');
            const retryResponse = await fetch(`${backendUrl}/verifyToken/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${newToken}` },
              credentials: 'include',
              body: JSON.stringify({}),  // Safari requires a body when Content-Type is application/json
            });
            if (retryResponse.ok) {
              logger.info('[AzureAuth] Token verified with backend after refresh');
              backendVerifiedRef.current = true;
              return;
            }
            // Check if retry got 403 (user not authorized)
            if (retryResponse.status === 403) {
              let errorMessage = 'User not authorized to access this application';
              try {
                const errorData = await retryResponse.json();
                if (errorData.error) errorMessage = errorData.error;
              } catch {}
              logger.error('[AzureAuth] User not authorized (403) - forcing logout');
              await authServiceRef.current?.signOut();
              throw new Error(errorMessage);
            }
            // Retry also failed with 401 - but DON'T force logout
            // The user still has a valid Azure session, the backend might have a transient issue
            logger.warn('[AzureAuth] Backend verification retry also failed - continuing without forced logout (backend may have transient issue)');
            return;
          }
          // Token refresh returned null - still don't force logout, let the token refresh flow handle it
          logger.warn('[AzureAuth] Token refresh returned null during backend verification - session may need re-authentication');
          return;
        }
        logger.warn(`[AzureAuth] Backend verification returned unexpected status=${response.status}`);
        throw new Error('Token verification failed');
      }
      logger.info('[AzureAuth] Token verified with backend');
      backendVerifiedRef.current = true;
    } catch (err: any) {
      logger.error('[AzureAuth] Backend token verification error:', err);
      setError(err.message);
      // Only call onError for authorization errors (403) that trigger logout
      // Don't call onError for transient errors to prevent double-logout
      const isAuthorizationError = err.message?.includes('not authorized');
      if (isAuthorizationError) {
        onError?.(err);
      } else {
        logger.warn('[AzureAuth] Continuing with Azure token despite backend verification failure (transient error)');
      }
    } finally {
      verifyInProgressRef.current = false;
    }
  }, [backendUrl, logger, onError]);

  const getToken = useCallback(async (forceRefresh = false) => {
    try {
      const token = await authServiceRef.current?.getToken(forceRefresh);
      if (token !== idToken) setIdToken(token || null);
      return token || null;
    } catch (err: any) {
      logger.error('Failed to get token:', err);
      setError(err.message);
      return null;
    }
  }, [idToken, logger]);

  const signOut = useCallback(async () => {
    try {
      await authServiceRef.current?.signOut();
      setUser(null);
      setIdToken(null);
      setError(null);
      setIsLoading(false);
      backendVerifiedRef.current = false;

      // Force a complete cleanup and re-initialization of the auth service
      // to prevent any stale state from persisting
      const currentConfig = {
        provider,
        firebaseAuth,
        msalConfig,
        tokenRefreshInterval,
        onTokenRefresh: (newToken: string | null) => {
          if ('debug' in (logger || {}) && typeof (logger as any).debug === 'function') {
            (logger as any).debug('Token refreshed via hook');
          }
          setIdToken(newToken);
        },
        onAuthStateChange: (user: any, token: string | null) => {
          setUser(user);
          setIdToken(token);
          setIsLoading(false);
          if (user && token && backendUrl && !verifyInProgressRef.current && !backendVerifiedRef.current) {
            verifyTokenWithBackend(token);
          }
        },
        logger,
      };
      
      // Destroy current service and create new one to ensure clean state
      authServiceRef.current?.destroy();
      authServiceRef.current = null;
      
      // Small delay to ensure cleanup is complete
      setTimeout(() => {
        const { UnifiedAuthenticationService } = require('../lib/UnifiedAuthenticationService');
        authServiceRef.current = new UnifiedAuthenticationService(currentConfig);
      }, 100);
      
    } catch (err: any) {
      logger.error('Sign out failed:', err);
      setError(err.message);
    }
  }, [logger, provider, firebaseAuth, msalConfig, tokenRefreshInterval, backendUrl, verifyTokenWithBackend]);

  const authenticatedFetch = useCallback(async (url: RequestInfo | URL, options: RequestInit = {}) => {
    if (!authServiceRef.current) throw new Error('Authentication service not initialized');
    try {
      const response = await authServiceRef.current.authenticatedFetch(url, options);
      return response;
    } catch (err: any) {
      logger.error('Authenticated fetch failed:', err);
      setError(err.message);
      throw err;
    }
  }, [logger]);

  const loginPopup = useCallback(async () => {
    if (!authServiceRef.current) return;
    try {
      await authServiceRef.current.loginPopup();
    } catch (err: any) {
      logger.error('Login popup failed:', err);
      setError(err.message);
    }
  }, [logger]);

  const loginRedirect = useCallback(async () => {
    if (!authServiceRef.current) return;
    try {
      await authServiceRef.current.loginRedirect();
    } catch (err: any) {
      logger.error('Login redirect failed:', err);
      setError(err.message);
    }
  }, [logger]);

  // Method to switch between providers (for mixed mode)
  const switchProvider = useCallback(async (newProvider: 'firebase' | 'azure') => {
    if (!authServiceRef.current) return;
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if the service supports switching
      if (typeof authServiceRef.current.switchProvider === 'function') {
        await authServiceRef.current.switchProvider(newProvider);
        logger.info(`Switched to ${newProvider} authentication`);
      } else {
        // Fallback: recreate the service with new provider
        authServiceRef.current.destroy();
        const { UnifiedAuthenticationService } = require('../lib/UnifiedAuthenticationService');
        authServiceRef.current = new UnifiedAuthenticationService({
          ...{ provider: newProvider, firebaseAuth, msalConfig, tokenRefreshInterval, logger },
          onTokenRefresh: (newToken: string | null) => {
            if ('debug' in (logger || {}) && typeof (logger as any).debug === 'function') {
              (logger as any).debug('Token refreshed via hook');
            }
            setIdToken(newToken);
          },
          onAuthStateChange: (user: any, token: string | null) => {
            setUser(user);
            setIdToken(token);
            setIsLoading(false);
            if (user && token && backendUrl && !verifyInProgressRef.current && !backendVerifiedRef.current) {
              verifyTokenWithBackend(token);
            }
          },
        });
      }
    } catch (err: any) {
      logger.error('Provider switch failed:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [logger, firebaseAuth, msalConfig, tokenRefreshInterval, backendUrl, verifyTokenWithBackend]);

  return { 
    user, 
    idToken, 
    isLoading, 
    error, 
    isAuthenticated: !!(user && idToken), 
    getToken, 
    signOut, 
    authenticatedFetch, 
    clearError: () => setError(null), 
    loginPopup, 
    loginRedirect,
    switchProvider,
    currentProvider: authServiceRef.current?.getProvider?.() || provider
  };
};
